import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useGetMakeupSettings } from '~entities/makeup/api/get-makeup-settings';
import { useUpdateMakeupSettings } from '~entities/makeup/api/update-makeup-settings';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import { Switch } from '~shared/shadcn/ui/switch';

export function MakeupClassesSettings() {
  const { data: makeupSettings, isLoading: isLoadingMakeupSettings } =
    useGetMakeupSettings();
  const updateMakeupSettingsMutation = useUpdateMakeupSettings();
  const [makeupEnabled, setMakeupEnabled] = useState(false);
  const [allowParentMakeupBooking, setAllowParentMakeupBooking] =
    useState(false);
  const [defaultFamilyMakeupCredits, setDefaultFamilyMakeupCredits] =
    useState(10);
  const [defaultStudentMakeupAllotment, setDefaultStudentMakeupAllotment] =
    useState(5);
  const [makeupExpirationDays, setMakeupExpirationDays] = useState(60);
  const [
    makeupConfirmationEmailEnabled,
    setMakeupConfirmationEmailEnabled,
  ] = useState(true);

  useEffect(() => {
    if (!makeupSettings) return;
    setMakeupEnabled(makeupSettings.makeupEnabled);
    setAllowParentMakeupBooking(makeupSettings.allowParentMakeupBooking);
    setDefaultFamilyMakeupCredits(makeupSettings.defaultFamilyMakeupCredits);
    setDefaultStudentMakeupAllotment(
      makeupSettings.defaultStudentMakeupAllotment,
    );
    setMakeupExpirationDays(makeupSettings.makeupExpirationDays);
    setMakeupConfirmationEmailEnabled(
      makeupSettings.makeupConfirmationEmailEnabled,
    );
  }, [makeupSettings]);

  const handleSaveMakeupSettings = () => {
    if (isLoadingMakeupSettings || !makeupSettings) {
      return;
    }

    if (
      defaultFamilyMakeupCredits < 0 ||
      defaultStudentMakeupAllotment < 0 ||
      makeupExpirationDays < 0
    ) {
      toast.error('Makeup limits and expiration days cannot be negative.');
      return;
    }

    updateMakeupSettingsMutation.mutate(
      {
        makeupEnabled,
        // Parent booking cannot stay enabled when makeups are disabled.
        allowParentMakeupBooking: makeupEnabled
          ? allowParentMakeupBooking
          : false,
        defaultFamilyMakeupCredits,
        defaultStudentMakeupAllotment,
        makeupExpirationDays,
        makeupConfirmationEmailEnabled,
      },
      {
        onSuccess: () => {
          if (!makeupEnabled) {
            setAllowParentMakeupBooking(false);
          }
          toast.success('Makeup settings have been updated.');
        },
        onError: () => {
          toast.error('Failed to update makeup settings. Please try again.');
        },
      },
    );
  };

  return (
    <div className='p-4'>
      <Link to='/dashboard' className='mb-8 flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <span className='text-lg font-semibold'>Back to Dashboard</span>
      </Link>

      <Card className='mx-auto max-w-4xl'>
        <CardHeader>
          <CardTitle>Makeup Classes</CardTitle>
          <CardDescription>
            Configure makeup booking for staff and parents, family credits, and
            student allotments per session
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='space-y-4'>
            <div className='rounded-lg border bg-card p-5'>
              <div className='flex items-center justify-between'>
                <div className='space-y-1'>
                  <label className='text-sm font-medium'>Enable makeups</label>
                  <p className='text-xs text-muted-foreground'>
                    Allow staff to book makeup classes from attendance
                  </p>
                </div>
                <Switch
                  checked={makeupEnabled}
                  onCheckedChange={setMakeupEnabled}
                  disabled={isLoadingMakeupSettings}
                  aria-label='Enable makeup booking'
                />
              </div>
            </div>

            <div className='rounded-lg border bg-card p-5'>
              <div className='flex items-center justify-between'>
                <div className='space-y-1'>
                  <label className='text-sm font-medium'>
                    Allow parent booking
                  </label>
                  <p className='text-xs text-muted-foreground'>
                    Let parents book makeups from absences in the Parent Portal
                    (requires Enable makeups)
                  </p>
                </div>
                <Switch
                  checked={allowParentMakeupBooking}
                  onCheckedChange={setAllowParentMakeupBooking}
                  disabled={isLoadingMakeupSettings || !makeupEnabled}
                  aria-label='Allow parent makeup booking'
                />
              </div>
            </div>

            <div className='rounded-lg border bg-card p-5'>
              <div className='grid gap-4 sm:grid-cols-3'>
                <div className='space-y-2'>
                  <label
                    htmlFor='defaultFamilyMakeupCredits'
                    className='text-sm font-medium'
                  >
                    Family credits (default)
                  </label>
                  <Input
                    id='defaultFamilyMakeupCredits'
                    type='number'
                    min={0}
                    value={defaultFamilyMakeupCredits}
                    onChange={(event) => {
                      setDefaultFamilyMakeupCredits(
                        Number(event.target.value) || 0,
                      );
                    }}
                    disabled={isLoadingMakeupSettings}
                  />
                  <p className='text-xs text-muted-foreground'>
                    Shared makeup pool per parent / session
                  </p>
                </div>

                <div className='space-y-2'>
                  <label
                    htmlFor='defaultStudentMakeupAllotment'
                    className='text-sm font-medium'
                  >
                    Student allotment (default)
                  </label>
                  <Input
                    id='defaultStudentMakeupAllotment'
                    type='number'
                    min={0}
                    value={defaultStudentMakeupAllotment}
                    onChange={(event) => {
                      setDefaultStudentMakeupAllotment(
                        Number(event.target.value) || 0,
                      );
                    }}
                    disabled={isLoadingMakeupSettings}
                  />
                  <p className='text-xs text-muted-foreground'>
                    Max makeups one student may use per session
                  </p>
                </div>

                <div className='space-y-2'>
                  <label
                    htmlFor='makeupExpirationDays'
                    className='text-sm font-medium'
                  >
                    Expiration (days)
                  </label>
                  <Input
                    id='makeupExpirationDays'
                    type='number'
                    min={0}
                    value={makeupExpirationDays}
                    onChange={(event) => {
                      setMakeupExpirationDays(Number(event.target.value) || 0);
                    }}
                    disabled={isLoadingMakeupSettings}
                  />
                  <p className='text-xs text-muted-foreground'>
                    Days after absence to book (0 = no expiry)
                  </p>
                </div>
              </div>
            </div>

            <div className='rounded-lg border bg-card p-5'>
              <div className='flex items-center justify-between'>
                <div className='space-y-1'>
                  <label className='text-sm font-medium'>
                    Confirmation email
                  </label>
                  <p className='text-xs text-muted-foreground'>
                    Email the family when a makeup is booked
                  </p>
                </div>
                <Switch
                  checked={makeupConfirmationEmailEnabled}
                  onCheckedChange={setMakeupConfirmationEmailEnabled}
                  disabled={isLoadingMakeupSettings}
                  aria-label='Enable makeup confirmation emails'
                />
              </div>
            </div>
          </div>

          <div className='flex justify-end pt-4'>
            <Button
              onClick={handleSaveMakeupSettings}
              disabled={
                isLoadingMakeupSettings ||
                !makeupSettings ||
                updateMakeupSettingsMutation.isPending
              }
            >
              {updateMakeupSettingsMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Saving Makeup Settings
                </>
              ) : (
                'Save Makeup Settings'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
